/**
 * Ceny, terminy i statystyki w panelu „Klienci” (prośby właściciela 2026-10-11, myślenie jak firma PV).
 *
 * Wszystko siedzi w `dane` karty klienta (bez migracji): `dane.cena`, `dane.terminy`, `dane.narzedzia`.
 * Backend wybiera z nich do skrótu listy `cena` ({netto, brutto}) i `terminy` ({spotkanie, spotkanie_ustalone,
 * montaz, montaz_ustalony}), `accounts/api.py` `_handel_klienta`. Czyste funkcje: z tych samych reguł liczą
 * lista, kanban, kafelki statystyk, wykres prognozy i karta klienta, więc kwoty nigdzie się nie rozjadą.
 *
 * Zestawy z Projektu 3D (`payload.variant === 'projekt_3d'`) nie mają cen: każda kwota może być null.
 */

/** Kwota z pola („43 200,50”, 40000) albo null; ujemne i nieliczby to brak kwoty. */
export const kwota = (v) => {
  if (v == null || v === '') return null
  const x = typeof v === 'number' ? v : Number(String(v).replace(/\s/g, '').replace(',', '.'))
  return Number.isFinite(x) && x >= 0 ? Math.round(x * 100) / 100 : null
}

const zaokr = (x) => Math.round(x * 100) / 100

/** Stawki VAT do wyboru przy cenie wpisanej ręcznie: dom do 300 m² to zwykle 8%, reszta 23%. */
export const STAWKI_VAT = [8, 23]
export const VAT_DOMYSLNY = 8

/** Brutto z netto i netto z brutto przy jednej stawce. */
export const bruttoZNetto = (netto, vat) => (kwota(netto) == null ? null : zaokr(kwota(netto) * (1 + Number(vat) / 100)))
export const nettoZBrutto = (brutto, vat) => (kwota(brutto) == null ? null : zaokr(kwota(brutto) / (1 + Number(vat) / 100)))

/** Cena z karty w jednym kształcie. `tryb`: „reczna” (wpisana) albo „kalkulator” (z ustawień firmy). */
export const cenaZDanych = (c) => {
  const d = c && typeof c === 'object' ? c : {}
  return {
    tryb: d.tryb === 'kalkulator' ? 'kalkulator' : 'reczna',
    netto: kwota(d.netto),
    brutto: kwota(d.brutto),
    vat: STAWKI_VAT.includes(Number(d.vat)) ? Number(d.vat) : VAT_DOMYSLNY,
    skladniki: d.skladniki && typeof d.skladniki === 'object' ? d.skladniki : null,
    // Usługi dodatkowe firmy w tej cenie (id z `extra_services`, runda 6); zapisuje je „Ustaw jako cenę klienta”.
    uslugi: Array.isArray(d.uslugi) ? d.uslugi.filter((u) => typeof u === 'string' && u) : [],
  }
}

/** Terminy z karty: daty RRRR-MM-DD i znacznik „ustalony” (sztywny termin zamiast przewidywanego). */
export const terminyZDanych = (t) => {
  const d = t && typeof t === 'object' ? t : {}
  const data = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '')
  return {
    spotkanie: data(d.spotkanie), spotkanieUstalone: Boolean(d.spotkanieUstalone),
    montaz: data(d.montaz), montazUstalony: Boolean(d.montazUstalony),
  }
}

/** Zgłoszenia widoczne w karcie danego klienta (nie każdy klient potrzebuje OSD, PSP czy PME2). */
export const NARZEDZIA = [
  ['osd', 'Zgłoszenie do operatora sieci'],
  ['psp', 'Zawiadomienie do PSP'],
  ['pme2', 'Dotacja PME2'],
]
export const narzedziaZDanych = (n) => {
  const d = n && typeof n === 'object' ? n : {}
  return Object.fromEntries(NARZEDZIA.map(([k]) => [k, d[k] !== false]))
}

/** Skrót z backendu w kształcie do sum: {netto, brutto} albo null. */
const cenaSkrotu = (k) => (k?.cena ? { netto: kwota(k.cena.netto), brutto: kwota(k.cena.brutto) } : null)

/**
 * Kafelki pod listą i kanbanem: na etap liczba klientów i zsumowane kwoty, plus „Razem”.
 * `zCena` mówi, ilu klientów ma wpisaną cenę: suma bez połowy cen byłaby myląca, więc kafelek to pokazuje.
 */
export const statystykiEtapow = (klienci, etapy) => {
  const policz = (lista) => lista.reduce((s, k) => {
    const c = cenaSkrotu(k)
    return {
      liczba: s.liczba + 1,
      zCena: s.zCena + (c && (c.netto != null || c.brutto != null) ? 1 : 0),
      netto: s.netto + (c?.netto || 0),
      brutto: s.brutto + (c?.brutto || 0),
    }
  }, { liczba: 0, zCena: 0, netto: 0, brutto: 0 })
  const ids = new Set((etapy || []).map((e) => e.id))
  const wiersze = (etapy || []).map((e) => ({ id: e.id, nazwa: e.nazwa, kolor: e.kolor, ...policz(klienci.filter((k) => k.status === e.id)) }))
  const bez = klienci.filter((k) => !ids.has(k.status))
  if (bez.length) wiersze.unshift({ id: '', nazwa: 'Bez etapu', kolor: 'gray', ...policz(bez) })
  return { etapy: wiersze, razem: policz(klienci) }
}

/**
 * Zakresy osi czasu (właściciel 2026-10-11): od wybranej daty początku (domyślnie dziś) tydzień albo miesiąc do przodu.
 * Przeszłość ogląda się, cofając datę początku w kalendarzu. Kubełki dzienne.
 */
export const ZAKRESY = {
  tydzien: { etykieta: 'Tydzień', dni: 7 },
  miesiac: { etykieta: 'Miesiąc', dni: 30 },
}

const isoDnia = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const plusDni = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return isoDnia(d) }

/**
 * Terminy klienta na oś czasu: z kalendarza (`kalendarz`, zaplanowane i zakończone; ustalony to pewny albo zakończony),
 * a dla starszych kart z pojedynczych dat (`terminy`). Montaż liczy się w dniu rozpoczęcia.
 */
const terminyOsi = (k) => {
  if (Array.isArray(k.kalendarz)) {
    // `rozpoczety` (montaż w toku, runda 4 2026-10-11) liczy się jak pewny.
    return k.kalendarz.filter((t) => ['zaplanowany', 'rozpoczety', 'zakonczony'].includes(t.status || 'zaplanowany'))
      .filter((t) => t.rodzaj === 'spotkanie' || t.rodzaj === 'montaz')
      .map((t) => ({ rodzaj: t.rodzaj, data: t.od, ustalony: Boolean(t.ustalony) || t.status === 'rozpoczety' || t.status === 'zakonczony' }))
  }
  const t = k.terminy || {}
  return [
    t.spotkanie && { rodzaj: 'spotkanie', data: t.spotkanie, ustalony: Boolean(t.spotkanie_ustalone) },
    t.montaz && { rodzaj: 'montaz', data: t.montaz, ustalony: Boolean(t.montaz_ustalony) },
  ].filter(Boolean)
}

/**
 * Oś czasu spotkań i montaży od `od` (RRRR-MM-DD) przez tydzień albo miesiąc, po dniach: liczba spotkań i montaży oraz
 * kwota brutto montaży (cena klienta raz, przy pierwszym montażu w oknie), osobno ustalone i przewidywane. `przeszly`
 * to dzień przed dziś. `zaleglych` liczy przewidywane terminy sprzed dziś w całej firmie (sygnał do porządku).
 */
export const osCzasu = (klienci, { od, zakres = 'miesiac', dzis = new Date() } = {}) => {
  const z = ZAKRESY[zakres] || ZAKRESY.miesiac
  const dzisIso = isoDnia(dzis)
  const start = od || dzisIso
  const kubelki = Array.from({ length: z.dni }, (_, i) => {
    const iso = plusDni(start, i)
    const d = new Date(`${iso}T12:00:00`)
    return {
      od: iso, do: iso, etykieta: `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`,
      przeszly: iso < dzisIso, dzis: iso === dzisIso,
      spotkaniaUstalone: 0, spotkaniaPrzewidywane: 0, montazeUstalone: 0, montazePrzewidywane: 0,
      kwotaUstalona: 0, kwotaPrzewidywana: 0, bezCeny: 0,
    }
  })
  const kubelek = (iso) => kubelki.find((k) => k.od === iso)
  let zaleglych = 0
  for (const k of klienci) {
    let cenaPoliczona = false
    for (const t of terminyOsi(k).sort((a, b) => a.data.localeCompare(b.data))) {
      if (!t.ustalony && t.data < dzisIso) zaleglych += 1
      const b = kubelek(t.data)
      if (!b) continue
      if (t.rodzaj === 'spotkanie') { b[t.ustalony ? 'spotkaniaUstalone' : 'spotkaniaPrzewidywane'] += 1; continue }
      b[t.ustalony ? 'montazeUstalone' : 'montazePrzewidywane'] += 1
      if (cenaPoliczona) continue
      cenaPoliczona = true
      const brutto = cenaSkrotu(k)?.brutto
      if (brutto == null) b.bezCeny += 1
      else b[t.ustalony ? 'kwotaUstalona' : 'kwotaPrzewidywana'] += brutto
    }
  }
  for (const k of kubelki) k.kwotaRazem = k.kwotaUstalona + k.kwotaPrzewidywana
  const suma = (pole, warunek) => kubelki.filter(warunek).reduce((s, k) => s + k[pole], 0)
  const montaze = (w) => suma('montazeUstalone', w) + suma('montazePrzewidywane', w)
  return {
    kubelki,
    zaleglych,
    pusta: kubelki.every((k) => !k.spotkaniaUstalone && !k.spotkaniaPrzewidywane && !k.montazeUstalone && !k.montazePrzewidywane),
    wstecz: { kwota: suma('kwotaRazem', (k) => k.przeszly), montaze: montaze((k) => k.przeszly) },
    naprzod: { kwota: suma('kwotaRazem', (k) => !k.przeszly), montaze: montaze((k) => !k.przeszly) },
  }
}

/** Strona listy: `nr` od zera, poza zakresem przycinane do ostatniej. */
export const NA_STRONE = 10
export const strona = (lista, nr, naStrone = NA_STRONE) => {
  const stron = Math.max(1, Math.ceil(lista.length / naStrone))
  const n = Math.min(Math.max(0, nr), stron - 1)
  return { pozycje: lista.slice(n * naStrone, (n + 1) * naStrone), nr: n, stron, od: lista.length ? n * naStrone + 1 : 0, do: Math.min(lista.length, (n + 1) * naStrone) }
}

// Słowa rodzaju produktu na początku nazw sklepowych („Moduł Fotowoltaiczny JA Solar 460W…”): rodzaj i tak
// stoi w kolumnie obok, a bez nich początek nazwy niesie producenta i model.
const RODZAJ_NA_POCZATKU = /^(?:moduł|modul|panel|inwerter|falownik|magazyn energii|moduł bateryjny|modul bateryjny|bateria|akumulator)(?:\s+(?:fotowoltaiczny|słoneczny|pv|hybrydowy|sieciowy|jednofazowy|trójfazowy|trojfazowy|bateryjny|energii))*\s+/i

/** Początek nazwy sprzętu do kafelka zestawu: bez słów rodzaju na początku, najwyżej `max` znaków z „…”. */
export const krotkaNazwaSprzetu = (nazwa, max = 50) => {
  // „Magazyn energii - Kon-TEC - MANA 10”: separatory „ - ” to spacje (myślnik w kodzie modelu zostaje)
  const pelna = String(nazwa || '').replace(/\s+[-–]\s+/g, ' ').replace(/\s+/g, ' ').trim()
  const bez = pelna.replace(RODZAJ_NA_POCZATKU, '').replace(/^[\s\-–:]+/, '')
  const n = bez.length >= 4 ? bez : pelna
  return n.length > max ? `${n.slice(0, max - 1).trimEnd()}…` : n
}

/** Rodzaj i parametr pozycji zestawu do kolumn „typ” i „moc”. */
export const RODZAJE_POZYCJI = { panels: 'Moduły', inverter: 'Falownik', battery: 'Magazyn', bms: 'BMS' }
export const mocPozycji = (it) => {
  const s = it?.spec || {}
  const liczba = (x, j) => `${String(Math.round(x * 100) / 100).replace('.', ',')} ${j}`
  if (it?.category === 'panels' && s.power_w) return liczba(s.power_w, 'W')
  if (it?.category === 'inverter' && (s.power_kw || s.power_w)) return liczba(s.power_kw || s.power_w / 1000, 'kW')
  if (it?.category === 'battery' && s.capacity_kwh) return liczba(s.capacity_kwh, 'kWh')
  return ''
}

/**
 * Stan sprzętu osobno dla każdego składnika zestawu (prośba właściciela 2026-10-11: moduły potrafią przyjechać
 * innego dnia niż falownik i magazyn). `dane.sprzet.pozycje` = {panels: {zamowiony, dojechal}, inverter: …}, klucz to
 * rodzaj pozycji zestawu. Karty z pierwszej wersji (jeden znacznik na cały sprzęt) dziedziczą go dla każdej pozycji.
 */
/**
 * Stan składnika (decyzja właściciela 2026-10-11, pulpit firmy): pięć stanów zamiast „zamówiony” i „dojechał”.
 * `zamowiony` i `dojechal` zostają obok jako pochodne (lista, kanban, powiadomienia i przepływ etapów liczą z nich):
 * zamówione to wszystko poza „do zamówienia”, dojechało to „w magazynie firmy” i „u klienta”.
 * Stare wpisy bez `stan`: „dojechał” czytamy jako „u klienta”, „zamówiony” jako „zamówione”.
 */
export const STANY_SPRZETU = [
  { id: 'do_zamowienia', nazwa: 'Do zamówienia', klasa: 'bg-red-50 text-red-700 ring-red-200' },
  { id: 'zamowione', nazwa: 'Zamówione', klasa: 'bg-amber-50 text-amber-800 ring-amber-200' },
  { id: 'w_drodze', nazwa: 'W drodze', klasa: 'bg-sky-50 text-sky-800 ring-sky-200' },
  { id: 'magazyn', nazwa: 'W magazynie firmy', klasa: 'bg-violet-50 text-violet-800 ring-violet-200' },
  { id: 'u_klienta', nazwa: 'U klienta', klasa: 'bg-green-50 text-green-800 ring-green-200' },
]
const ID_STANOW = new Set(STANY_SPRZETU.map((s) => s.id))
export const stanSprzetu = (id) => STANY_SPRZETU.find((s) => s.id === id) || STANY_SPRZETU[0]
export const zStanu = (stan) => ({ stan, zamowiony: stan !== 'do_zamowienia', dojechal: stan === 'magazyn' || stan === 'u_klienta' })

export const pozycjeSprzetu = (sprzet, rodzaje) => Object.fromEntries(rodzaje.map((r) => {
  const p = sprzet?.pozycje?.[r]
  if (p && ID_STANOW.has(p.stan)) return [r, zStanu(p.stan)]
  const dojechal = Boolean(p ? p.dojechal : sprzet?.dojechal)
  const zamowiony = dojechal || Boolean(p ? p.zamowiony : sprzet?.zamowiony)
  return [r, zStanu(dojechal ? 'u_klienta' : zamowiony ? 'zamowione' : 'do_zamowienia')]
}))

/** Zapis do karty: pozycje i podsumowanie dla listy i kanbanu (wszystko zamówione / dojechało, ile z ilu). */
export const sprzetZPozycji = (pozycje) => {
  const w = Object.values(pozycje)
  const zamowione = w.filter((p) => p.zamowiony).length
  const dojechalo = w.filter((p) => p.dojechal).length
  return {
    pozycje, wszystkich: w.length, zamowione, dojechalo,
    zamowiony: w.length > 0 && zamowione === w.length, dojechal: w.length > 0 && dojechalo === w.length,
  }
}

/**
 * Sprzęt wgrany z pliku importu klientów (`dane.sprzetZImportu` {moduly, falownik, magazyn, konstrukcja}, 2026-10-11):
 * tekst z arkusza bez dopasowania do katalogu, więc bez stanów zamówienia. Karta i pulpit pokazują go obok zestawu.
 */
export const RODZAJE_Z_IMPORTU = [['moduly', 'Moduły'], ['falownik', 'Falownik'], ['magazyn', 'Magazyn'], ['konstrukcja', 'Konstrukcja']]
export const pozycjeZImportu = (s) => (s && typeof s === 'object'
  ? RODZAJE_Z_IMPORTU.filter(([k]) => typeof s[k] === 'string' && s[k].trim()).map(([k, nazwa]) => ({ rodzaj: k, nazwa, opis: s[k].trim() }))
  : [])

/** Krótki opis stanu sprzętu na kartę kanbanu; null, gdy nic jeszcze nie zamówione. */
export const opisSprzetu = (s) => {
  if (!s) return null
  const z = (n) => (s.wszystkich > 1 ? ` ${n} z ${s.wszystkich}` : '')
  if (s.dojechal) return 'sprzęt na miejscu'
  if (s.dojechalo > 0) return `dojechało${z(s.dojechalo)}`
  if (s.zamowiony) return 'sprzęt zamówiony'
  if (s.zamowione > 0) return `zamówiono${z(s.zamowione)}`
  return null
}
